import { NewRelic } from "@dealer-center/core-third-party-newrelic";
import { usePendo } from "@dealer-center/core-third-party-pendo";
import { useEffect } from "react";
import { TagManager } from "@dealer-center/core-third-party-gtm";
import { Outlet } from "react-router-dom";
import { useSystemStore } from "@dealer-center/ddc-state-system";
import type { GenericFlags } from "@dealer-center/core-interfaces-third-party-launch-darkly";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import type { CC9FlagNames } from "@dealer-center/core-interfaces-third-party-launch-darkly";
import { useGetAccount } from "@dealer-center/ddc-api-account-fetch";
import { useGetUser } from "@dealer-center/ddc-api-user-fetch";
import { convertRecordStringBooleanToStringArray } from "@dealer-center/core-utils";

export const AppHooks = () => {
  const userState = useGetUser();
  const { config, enviroment } = useSystemStore();
  const { data: userEntity, isLoading: isUserLoading } = userState;
  const { initialize: initializePendo } = usePendo();
  const { data: accountEntity } = useGetAccount();

  const launchDarklyFlags: GenericFlags<CC9FlagNames> = useCC9Flags();
  useEffect(() => {
    if (
      !isUserLoading &&
      userEntity?.user &&
      userEntity.internal !== undefined
    ) {
      if (config.gtmAccountId) {
        TagManager.initialize({
          dataLayer: {
            dataLayerReady: true,
            common: {
              dataLayerVersion: 2,
              context: {
                dealershipName: accountEntity?.name || "",
                dealershipId: accountEntity?.id
              },
              application: {
                name: "CC9-Landing",
                version: "1.0",
                environment: enviroment,
                isProduction: enviroment === "prod"
              },
              user: {
                bridgePlatformId: userEntity.user,
                isInternalUser: userEntity.internal,
                userType: userEntity.internal ? "internal" : "dealer",
                bridgeUsername: userEntity.user
              }
            }
          },
          dataLayerName: "dataLayer",
          gtmId: config.gtmAccountId
        });
      }

      if (initializePendo && config.pendo) {
        const permissions: string[] = convertRecordStringBooleanToStringArray(
          userEntity.userPermissions || {}
        );
        initializePendo({
          config: config.pendo,
          identify: {
            visitor: {
              permissions,
              bridgePlatformId: userEntity.userInfo?.platformId || "",
              internalUser: userEntity.internal,
              userFirstName: userEntity.first,
              userLastName: userEntity.last,
              bridgeUserName: userEntity.userInfo?.username || "",
              ddcUserName: userEntity.userInfo?.userId || "",
              launchDarklyToggles: Object.keys(launchDarklyFlags).filter(
                key => launchDarklyFlags[key as CC9FlagNames]
              ),
              userEmail: userEntity.userInfo?.email || undefined,
              product: "ddc"
            },
            account: {
              accountId: accountEntity?.id || "",
              accountName: accountEntity?.name
            }
          }
        });
      }
    }
  }, [
    initializePendo,
    accountEntity?.id,
    accountEntity?.name,
    config.gtmAccountId,
    config.pendo,
    enviroment,
    launchDarklyFlags,
    userEntity,
    isUserLoading
  ]);

  // NewRelic
  useEffect(() => {
    if (!config.newRelic || NewRelic.isInitialized()) {
      return;
    }
    NewRelic.initialize(config.newRelic);
  }, [config.newRelic]);

  return <Outlet />;
};
